import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

interface Client {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  tax_id: string | null;
  notes: string | null;
}

export function Clients() {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "", email: "", taxId: "", notes: "" });
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadClients = async () => {
    const data = await api("/clients");
    setClients(data);
  };

  useEffect(() => { loadClients(); }, []);

  const resetForm = () => {
    setForm({ name: "", address: "", phone: "", email: "", taxId: "", notes: "" });
    setEditingClient(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingClient) {
      await api(`/clients/${editingClient.id}`, { method: "PUT", body: JSON.stringify(form) });
    } else {
      await api("/clients", { method: "POST", body: JSON.stringify(form) });
    }
    resetForm();
    loadClients();
  };

  const handleEdit = (client: Client) => {
    setForm({
      name: client.name, address: client.address ?? "", phone: client.phone ?? "",
      email: client.email ?? "", taxId: client.tax_id ?? "", notes: client.notes ?? "",
    });
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteError(null);
    try {
      await api(`/clients/${id}`, { method: "DELETE" });
      setConfirmingDelete(null);
      loadClients();
    } catch (e: any) {
      if (e?.message === "hasActiveJobs") {
        setDeleteError(id);
      }
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
            {t("clients.title")}
          </h1>
          <p className="text-sm text-navy-400 mt-1">{t("clients.clientCount", { count: clients.length })}</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="btn btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t("clients.addClient")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-navy-100 p-6 mb-6 animate-slide-in">
          <h3 className="font-semibold mb-4" style={{ fontFamily: "var(--font-display)" }}>
            {editingClient ? t("clients.editClient") : t("clients.newClient")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="input-label">{t("clients.name")} *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input" />
            </div>
            <div>
              <label className="input-label">{t("clients.address")}</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" />
            </div>
            <div>
              <label className="input-label">{t("clients.phone")}</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
            </div>
            <div>
              <label className="input-label">{t("clients.email")}</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
            </div>
            <div>
              <label className="input-label">{t("clients.taxId")}</label>
              <input value={form.taxId} onChange={(e) => setForm({ ...form, taxId: e.target.value })} className="input" placeholder="NIP" />
            </div>
          </div>
          <div className="mb-4">
            <label className="input-label">{t("clients.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input" style={{ resize: "vertical" }} />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn btn-primary">{t("common.save")}</button>
            <button type="button" onClick={resetForm} className="btn btn-ghost">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      {clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-navy-100 p-16 text-center">
          <div className="w-14 h-14 bg-navy-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          </div>
          <p className="text-navy-600 font-medium mb-1">{t("clients.noClientsTitle")}</p>
          <p className="text-navy-400 text-sm">{t("clients.noClients")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          {clients.map((client, i) => (
            <div key={client.id} className={`px-5 py-4 transition-colors ${i < clients.length - 1 ? "border-b border-navy-50" : ""}`}>
              {confirmingDelete === client.id ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-navy-600">{t("common.confirm")}</p>
                    {deleteError === client.id && (
                      <p className="text-xs text-rose-500 mt-1">{t("clients.cannotDeleteActiveJobs")}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => handleDelete(client.id)} className="btn btn-danger btn-sm">{t("common.delete")}</button>
                    <button onClick={() => { setConfirmingDelete(null); setDeleteError(null); }} className="btn btn-ghost btn-sm">{t("common.cancel")}</button>
                  </div>
                </div>
              ) : (
                <div className="group flex items-center justify-between hover:bg-navy-50/50">
                  <Link to={`/app/clients/${client.id}`} className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-900 group-hover:text-amber-600 transition-colors">{client.name}</p>
                    <p className="text-xs text-navy-400 truncate mt-0.5">
                      {[client.address, client.phone, client.email].filter(Boolean).join(" · ") || t("clients.noDetails")}
                    </p>
                  </Link>
                  <div className="flex gap-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEdit(client)} className="btn btn-ghost btn-sm">{t("common.edit")}</button>
                    <button onClick={() => { setDeleteError(null); setConfirmingDelete(client.id); }} className="btn btn-danger btn-sm">{t("common.delete")}</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
